// Morgan State University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Morgan State posts no Common Data Set (its Office of Institutional
//    Research pages offer a fact book, dashboards and consumer information,
//    none with a CDS; checked again 2026-10-01). The first-year counts come
//    from the U.S. Department of Education's IPEDS Admissions survey for
//    Fall 2024, first-time degree-seeking students (College Navigator and
//    the ADM2024 complete data file, unit 163453).
//  - Morgan Undergraduate Admission and Recruitment: Application Deadlines
//    (Fall 2027 freshman deadlines) and Freshman Applicants (test-optional
//    admission, the $35 fee, and the FAQ: fall decisions are made on a
//    rolling basis beginning in January). No set decision dates are
//    published. The site served these pages only to a browser, so they were
//    read with Playwright.
//  - Morgan Office of the Bursar, Schedule of Tuition and Fees for Fall 2026
//    - Spring 2027 (per semester, times two). Morgan posts no cost of
//    attendance page (its financial aid site has none), so the table holds
//    the billed tuition and fees only; health insurance, rooms and meal
//    plans, which vary by choice, are given as ranges in the note.
//  - College Scorecard, school id 163453 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=163453#admsns',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Morgan Undergraduate Admission and Recruitment, Application Deadlines',
        url: 'https://www.morgan.edu/office-of-undergraduate-admission-and-recruitment/how-to-apply/application-deadlines',
        read: '2026-10-01',
    },
    freshman: {
        label: 'Morgan Undergraduate Admission and Recruitment, Freshman Applicants',
        url: 'https://www.morgan.edu/office-of-undergraduate-admission-and-recruitment/how-to-apply/freshman-applicants',
        read: '2026-10-01',
    },
    tuition: {
        label: 'Morgan Office of the Bursar, Schedule of Tuition and Fees for Fall 2026 - Spring 2027',
        url: 'https://www.morgan.edu/bursar/tuition-and-fees/fall-2026-spring-2027',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?163453-Morgan-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'morgan-state-university',
    kbId: 'morgan_state_university',
    name: 'Morgan State University',
    shortName: 'Morgan State',
    location: 'Baltimore, MD',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Morgan official colors page); decoration only.
    branding: {
        primary: '#1B4383',
        secondary: '#F47937',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/morgan_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 23366,
        admitted: 19214,
        enrolled: 2377,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates (Morgan State posts no Common Data Set)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'IPEDS reports one first-year acceptance rate for the whole university, and Morgan State publishes no rate by school or major.',
        source: 'ipeds',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'April 1' },
        rows: [
            { plan: 'Priority application', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Application', deadline: 'April 1', decision: null, binding: false },
        ],
        note: 'Documents are due April 30. Applying by December 1 gives first-time, full-time freshmen the best consideration for the Troy E. Quinn Award (up to $6,000 a year); later applicants are considered if funding remains. Morgan does not publish decision dates: its freshman page says fall decisions are made on a rolling basis beginning in January.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Morgan State is test-optional: you may send official SAT or ACT scores, or apply without them and show a strong academic record instead (course rigor, grades, GPA and grade trends). Applying test-optional does not help or hurt your admission, and test-optional students can still be considered for merit scholarships and the Clara I. Adams Honors College.',
        source: 'freshman',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        partial: true,
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (12 or more semester hours)', amounts: [5935, 17265] },
            { item: 'Fees', amounts: [2531, 2531] },
        ],
        totals: [8466, 19796],
        note: 'Billed tuition and fees for a full-time undergraduate, fall 2026 and spring 2027. Morgan posts no full cost of attendance, so housing, food, books and other costs are not in this total. The same schedule lists health insurance for full-time undergraduates ($781.95 in fall and $1,159.05 in spring), on-campus double rooms from $4,117.50 to $6,385 a semester, and anytime dining plans from $2,847.50 to $3,437.50 a semester.',
        source: 'tuition',
        netPrice: {
            average: 14653,
            byIncome: [
                { income: '$0 to $30,000', amount: 14329 },
                { income: '$30,001 to $48,000', amount: 14998 },
                { income: '$48,001 to $75,000', amount: 16280 },
                { income: '$75,001 to $110,000', amount: 21345 },
                { income: 'Over $110,000', amount: 23864 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Morgan State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
